import type { TranslateService } from '@ngx-translate/core';
import type { OnyxCustomAddress } from '@onyx/angular';
import { OnyxMapRoutePointStatus } from '@onyx/angular';
import { chain, cloneDeep, isArray, orderBy } from 'lodash';
import { Duration } from 'luxon';
import { v7 as uuidv7 } from 'uuid';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { RouteHelper } from '../../../../../common/helpers/route.helper';
import type { TelematicsItem } from '../../../../../common/interfaces/telematics/telematics-item';
import type {
  TelematicsOrderRoutePoint,
  TelematicsRoutePoint,
} from '../../../../../common/interfaces/telematics/telematics-route-point';
import type { TelematicsRoutePointOrder } from '../../../../../common/interfaces/telematics/telematics-route-point-order';
import { TelematicsRoutePointStatus } from '../../../../../common/interfaces/telematics/telematics-vehicle-point-status';
import type { Driver } from '../../../../drivers/common/interfaces/driver';
import { FleetCategory } from '../../../../fleet/common/enums/fleet-category';
import { SemiTrailerSize } from '../../../../fleet/common/enums/semi-trailer-size';
import { TechnicalPointCategory } from '../../../../fleet/common/enums/technical-point-category';
import { SwapPointType } from '../../../../fleet/common/enums/technical-point-type';
import { TrailerSize } from '../../../../fleet/common/enums/trailer-size';
import type { Fleet } from '../../../../fleet/common/interfaces/fleet';
import { OrderPointCategory } from '../../../../orders/common/enums/order-point-category';
import { OrderPointStatus } from '../../../../orders/common/enums/order-point-status';
import { OrderTimeWindowType } from '../../../../orders/common/enums/order-time-window-type';
import type { Order } from '../../../../orders/common/interfaces/order';
import { SwapSetError } from '../../enums/swap/swap-error';
import { SwapSetAction } from '../../enums/swap/swap-set-action';
import { SwapUnassignPointReason } from '../../enums/swap/swap-unassign-point-reason';
import { SwapVehicleType } from '../../enums/swap/swap-vehicle-type';
import type { SwapFilters } from '../../interfaces/swap/swap-filters';
import type { SwapRoutePoint } from '../../interfaces/swap/swap-route-point';
import type { SwapSet, SwapSetData } from '../../interfaces/swap/swap-set';
import type { SwapUnassignedPoint } from '../../interfaces/swap/swap-unassigned-points';
import type { SwapState, SwapVehicleData } from '../../stores/swap.store';
import { SwapFormHelper } from './swap-form.helper';

export class SwapHelper {
  public static getPointUuid(
    point: SwapRoutePoint | SwapUnassignedPoint,
  ): string {
    return 'isSwapPoint' in point && point.isSwapPoint
      ? point.point.uuid
      : point.point.point.uuid;
  }

  public static getPointOrderUuid(
    point: SwapRoutePoint | SwapUnassignedPoint,
  ): string | null {
    return 'isSwapPoint' in point && point.isSwapPoint
      ? null
      : point.point.order.uuid;
  }

  public static isActivePoint(point: SwapRoutePoint): boolean {
    return (
      !point.isSwapPoint &&
      [OrderPointStatus.WAITING, OrderPointStatus.IN_PROGRESS].includes(
        point.point.point.status,
      )
    );
  }

  public static isDedicated(point: SwapRoutePoint): boolean {
    return (
      !point.isSwapPoint && RouteHelper.isOrderDedicated(point.point.order)
    );
  }

  public static getPointsUuid(points: SwapUnassignedPoint[]): string[] {
    return points.map(({ point }) => point.point.uuid);
  }

  public static getOtherType(type: SwapVehicleType): SwapVehicleType {
    return type === SwapVehicleType.PRIMARY
      ? SwapVehicleType.SECONDARY
      : SwapVehicleType.PRIMARY;
  }

  public static getSwapPointIndex(route: SwapRoutePoint[]): number {
    return route.findIndex((point) => point.isSwapPoint);
  }

  public static hasSwapPointMoved(
    vehicle: TelematicsItem | null,
    route: SwapRoutePoint[],
    isDouble: boolean,
    swapPointUuid: string,
  ): boolean {
    if (!vehicle) return false;

    const { route: defaultRoute } = SwapHelper.getVehicleRoute(
      vehicle,
      isDouble,
      swapPointUuid,
    );

    return (
      SwapHelper.getSwapPointIndex(route) !==
      SwapHelper.getSwapPointIndex(defaultRoute)
    );
  }

  public static getSwapPointUuid(route: SwapRoutePoint[]): string | null {
    return route.find((point) => point.isSwapPoint)?.point.uuid ?? null;
  }

  public static getRoutePoints(
    route: SwapRoutePoint[],
  ): TelematicsOrderRoutePoint[] {
    return route
      .filter((point) => !point.isSwapPoint)
      .map(({ point }) => point);
  }

  public static getOrdersUuid(route: SwapRoutePoint[]): Set<string> {
    return new Set(
      route
        .map((point) => SwapHelper.getPointOrderUuid(point))
        .filter((uuid) => !!uuid) as string[],
    );
  }

  public static createSet(
    vehicle: Fleet | null,
    route: SwapRoutePoint[],
    drivers: Driver[] = [],
    trailers: Fleet[] = [],
    orders: Order[] = [],
  ): SwapSet {
    const createSwapSetData = <Item>(
      value: Item | null,
      initialData?: Item | null,
    ): SwapSetData<Item> => ({
      value,
      ...(initialData ? { initialData } : {}),
      action: SwapSetAction.NONE,
    });

    const activeOrders = this.getActiveOrdersBeforeSwapPoint(route)
      .map((order) =>
        createSwapSetData(
          orders.find(({ uuid }) => uuid === order.uuid) ?? null,
        ),
      )
      .filter((order) => !!order);

    const activeDrivers = chain([
      vehicle?.drivers?.primaryDriver?.uuid,
      vehicle?.drivers?.secondaryDriver?.uuid,
    ])
      .compact()
      .filter((uuid) => !!uuid)
      .map((driverUuid) =>
        createSwapSetData(
          drivers.find(({ uuid }) => uuid === driverUuid) ?? null,
        ),
      )
      .compact()
      .value();

    const activeTrailer = vehicle?.trailer?.uuid
      ? (trailers.find(({ uuid }) => uuid === vehicle.trailer?.uuid) ?? null)
      : null;

    return {
      vehicle: createSwapSetData(vehicle),
      trailer: createSwapSetData(activeTrailer, activeTrailer),
      drivers: activeDrivers,
      activeOrders,
    };
  }

  public static syncActiveOrders(
    orders: SwapSetData<Order>[],
    route: SwapRoutePoint[],
    availableOrders: Order[],
    set: SwapSet,
  ): SwapSetData<Order>[] {
    const activeBeforeSwap = this.getActiveOrdersBeforeSwapPoint(route);
    const ordersOnRoute = this.getOrdersUuid(route);

    const ordersMap = new Map(
      orders.map((order) => [order.value?.uuid, order]),
    );
    const availableOrdersMap = new Map(
      availableOrders.map((order) => [order.uuid, order]),
    );

    const syncedFromRoute = activeBeforeSwap.map((order) => {
      const existingOrder = ordersMap.get(order.uuid);
      const availableOrder = availableOrdersMap.get(order.uuid);
      if (!existingOrder && !availableOrder) return null;

      return existingOrder
        ? { ...existingOrder, value: availableOrder ?? existingOrder.value }
        : { value: availableOrder ?? null, action: SwapSetAction.NONE };
    });

    const processedUuids = new Set(activeBeforeSwap.map((o) => o.uuid));

    const KEEP_ACTIONS = [
      SwapSetAction.UNLOAD_IN_POINT,
      SwapSetAction.TRANSFER,
      SwapSetAction.DROP_OFF_IN_POINT,
      SwapSetAction.DROP_OFF_ON_SEMI_TRAILER,
    ];

    const finalSyncedOrders = chain(syncedFromRoute)
      .compact()
      .concat(
        orders.filter((order) => {
          const uuid = order.value?.uuid;

          return (
            uuid &&
            !processedUuids.has(uuid) &&
            KEEP_ACTIONS.includes(order.action)
          );
        }),
      )
      .value();

    let ftlCount = 0;
    let ltlCount = 0;

    for (const { value, action } of finalSyncedOrders) {
      const uuid = value?.uuid;
      const isOnRoute = uuid && ordersOnRoute.has(uuid);
      const isDropping = [
        SwapSetAction.UNLOAD_IN_POINT,
        SwapSetAction.DROP_OFF_ON_SEMI_TRAILER,
      ].includes(action);

      if (isOnRoute && !isDropping) {
        RouteHelper.isOrderDedicated(value) ? ftlCount++ : ltlCount++;
      }
    }

    const hasCargoSpace = SwapHelper.hasCargoSpace(
      set.vehicle.value,
      set.trailer.value,
      set.trailer.action,
    );

    const ACTIVE_ORDER_ACTIONS = [
      SwapSetAction.NONE,
      SwapSetAction.TRANSFER,
      SwapSetAction.SWAP,
    ];

    return finalSyncedOrders.map((order) => {
      const uuid = order.value?.uuid;
      const isOnRoute = uuid && ordersOnRoute.has(uuid);
      const errors: SwapSetError[] = [];

      if (isOnRoute) {
        if (ftlCount > 0 && ltlCount > 0) {
          errors.push(SwapSetError.FTL_LTL_CONFLICT);
        }

        if (
          ftlCount > 1 &&
          order.value &&
          RouteHelper.isOrderDedicated(order.value)
        ) {
          errors.push(SwapSetError.MULTIPLE_FTL);
        }
      }

      if (
        !hasCargoSpace &&
        order.value &&
        ACTIVE_ORDER_ACTIONS.includes(order.action)
      ) {
        errors.push(SwapSetError.ACTIVE_ORDER_WITHOUT_CARGO_SPACE);
      }

      return { ...order, errors: errors.length ? errors : undefined };
    });
  }

  public static syncDrivers(
    drivers: SwapSetData<Driver>[],
    set: SwapSet,
  ): SwapSetData<Driver>[] {
    const initialMap = SwapHelper.getVehicleInitialDriversMap(
      set.vehicle.value,
    );
    const sortedDrivers = orderBy(
      drivers,
      [(driver) => initialMap.get(driver.value?.uuid ?? '') ?? Infinity],
      'asc',
    );
    const activeDriversCount = sortedDrivers.filter(
      ({ value, action }) =>
        value && action !== SwapSetAction.DROP_OFF_IN_POINT,
    ).length;

    return sortedDrivers.map((driver) => ({
      ...driver,
      errors:
        activeDriversCount > 2 ? [SwapSetError.TOO_MANY_DRIVERS] : undefined,
    }));
  }

  public static extractFreePoints(
    route: SwapRoutePoint[],
    existingPoints: SwapUnassignedPoint[],
    initialOrderMap: Map<string, number>,
    reason: SwapUnassignPointReason,
    shouldExtractFn: (point: SwapRoutePoint) => boolean,
  ): { newRoute: SwapRoutePoint[]; combinedPoints: SwapUnassignedPoint[] } {
    const swapIndex = SwapHelper.getSwapPointIndex(route);

    const freePoints: SwapRoutePoint[] = [];
    const newRoute: SwapRoutePoint[] = [];

    for (const [index, point] of route.entries()) {
      if (index <= swapIndex || point.isSwapPoint) {
        newRoute.push(point);
        continue;
      }

      if (shouldExtractFn(point)) {
        freePoints.push(point);
      } else {
        newRoute.push(point);
      }
    }

    if (!freePoints.length) {
      return { newRoute, combinedPoints: existingPoints };
    }

    const combinedPoints = orderBy(
      [
        ...existingPoints,
        ...freePoints
          .filter((point) => !point.isSwapPoint)
          .map(({ point }) => ({
            point,
            reason,
          })),
      ],
      (item) => initialOrderMap.get(SwapHelper.getPointUuid(item)) ?? Infinity,
      'asc',
    );

    return { newRoute, combinedPoints };
  }

  public static unassignFreeDedicatedPoint(
    route: SwapRoutePoint[],
    activeOrders: SwapSetData<Order>[],
    unassignedPoints: SwapUnassignedPoint[],
    initialOrderMap: Map<string, number>,
  ): { newRoute: SwapRoutePoint[]; newUnassigned: SwapUnassignedPoint[] } {
    const swapIndex = SwapHelper.getSwapPointIndex(route);
    const orderUuidsBeforeSwap = SwapHelper.getOrdersUuid(
      route.slice(0, swapIndex),
    );

    const { newRoute, combinedPoints } = this.extractFreePoints(
      route,
      unassignedPoints,
      initialOrderMap,
      SwapUnassignPointReason.POINT,
      (point) => {
        const orderUuid = SwapHelper.getPointOrderUuid(point);
        const order = activeOrders.find(
          ({ value }) => value?.uuid === orderUuid,
        );

        return (
          order?.action === SwapSetAction.UNLOAD_IN_POINT &&
          SwapHelper.isDedicated(point) &&
          orderUuidsBeforeSwap.has(orderUuid ?? '')
        );
      },
    );

    return { newRoute, newUnassigned: combinedPoints };
  }

  public static unassignFreeDedicatedTrailerPoint(
    route: SwapRoutePoint[],
    activeOrders: SwapSetData<Order>[],
    trailerPoints: SwapUnassignedPoint[],
    trailerAction: SwapSetAction,
    initialOrderMap: Map<string, number>,
  ): { newRoute: SwapRoutePoint[]; newTrailerPoints: SwapUnassignedPoint[] } {
    const hasDroppedTrailer = trailerAction === SwapSetAction.DROP_OFF_IN_POINT;

    const { newRoute, combinedPoints } = this.extractFreePoints(
      route,
      trailerPoints,
      initialOrderMap,
      SwapUnassignPointReason.SEMI_TRAILER,
      (point) => {
        if (hasDroppedTrailer) return true;

        const orderUuid = SwapHelper.getPointOrderUuid(point);
        const order = activeOrders.find(
          ({ value }) => value?.uuid === orderUuid,
        );

        return order?.action === SwapSetAction.DROP_OFF_ON_SEMI_TRAILER;
      },
    );

    return { newRoute, newTrailerPoints: combinedPoints };
  }

  public static getRevertOrders(
    route: SwapRoutePoint[],
    activeOrders: SwapSetData<Order>[],
    otherSetActiveOrders: SwapSetData<Order>[] = [],
  ): string[] {
    const swapIndex = SwapHelper.getSwapPointIndex(route);
    const beforeSwapOrdersUuid = SwapHelper.getOrdersUuid(
      route.slice(0, swapIndex),
    );

    const routeOrdersUuid = SwapHelper.getOrdersUuid(route);

    const sourceRevertedOrders = activeOrders
      .filter(
        ({ value, action }) =>
          value?.uuid &&
          action === SwapSetAction.UNLOAD_IN_POINT &&
          !beforeSwapOrdersUuid.has(value.uuid),
      )
      .map(({ value }) => value!.uuid);

    const targetRevertedOrders = otherSetActiveOrders
      .filter(
        ({ value, action }) =>
          value?.uuid &&
          action === SwapSetAction.TRANSFER &&
          routeOrdersUuid.has(value.uuid) &&
          !beforeSwapOrdersUuid.has(value.uuid),
      )
      .map(({ value }) => value!.uuid);

    return [...sourceRevertedOrders, ...targetRevertedOrders];
  }

  public static sortDedicatedBlocks(
    route: SwapRoutePoint[],
    initialOrderMap: Map<string, number>,
  ): SwapRoutePoint[] {
    const ordersMap = new Map<string, number>();
    for (const [index, point] of route.entries()) {
      const orderUuid = SwapHelper.getPointOrderUuid(point);
      if (orderUuid) ordersMap.set(orderUuid, index);
    }

    let activeOrderUuid: string | null;
    const routeBlocks = route.reduce(
      (blocks: SwapRoutePoint[][], currentPoint, index) => {
        const currentOrderUuid = SwapHelper.getPointOrderUuid(currentPoint);
        const isDedicated =
          SwapHelper.isDedicated(currentPoint) && !currentPoint.isSwapPoint;

        const isSwapPointInside =
          currentPoint.isSwapPoint &&
          !!activeOrderUuid &&
          (ordersMap.get(activeOrderUuid) ?? 0) > index;
        const isSameOrder = isDedicated && currentOrderUuid === activeOrderUuid;

        if ((isSwapPointInside || isSameOrder) && blocks.length > 0) {
          blocks[blocks.length - 1].push(currentPoint);
        } else {
          blocks.push([currentPoint]);
          activeOrderUuid = isDedicated ? currentOrderUuid : null;
        }
        return blocks;
      },
      [],
    );

    const swapPointIndex = routeBlocks.findIndex((block) =>
      block.some((point) => point.isSwapPoint),
    );
    if (swapPointIndex <= 0) return route;

    const sortCutoffIndex =
      routeBlocks[swapPointIndex].length === 1
        ? swapPointIndex
        : swapPointIndex + 1;

    return [
      ...orderBy(
        routeBlocks.slice(0, sortCutoffIndex),
        (block) => {
          const firstPoint = block.find((point) => !point.isSwapPoint);

          return firstPoint
            ? (initialOrderMap.get(SwapHelper.getPointUuid(firstPoint)) ??
                Infinity)
            : Infinity;
        },
        'asc',
      ).flat(),
      ...routeBlocks.slice(sortCutoffIndex).flat(),
    ];
  }

  public static validateRouteSequence(
    route: SwapRoutePoint[],
    transferredOrdersUuid: Set<string> = new Set(),
  ): boolean {
    const activeFtlOrders = new Map<string, number>(); // orderUuid -> active count
    const activeLtlOrders = new Map<string, number>(); // orderUuid -> active count
    const lastOrderPointIndex = new Map<string, number>(); // orderUuid -> last point index
    const orderPoints = new Map<string, TelematicsOrderRoutePoint[]>(); // orderUuid -> points

    const unloadedOrdersUuid = new Set<string>();
    const loadedOrdersUuid = new Set<string>(transferredOrdersUuid);

    for (const [index, item] of route.entries()) {
      if (item.isSwapPoint || !item.point.order?.uuid) continue;

      const orderUuid = item.point.order.uuid;
      lastOrderPointIndex.set(orderUuid, index);

      const points = orderPoints.get(orderUuid);
      if (points) {
        points.push(item.point);
      } else {
        orderPoints.set(orderUuid, [item.point]);
      }
    }
    let passedSwapPoint = false;

    for (const [index, item] of route.entries()) {
      if (item.isSwapPoint) {
        passedSwapPoint = true;
        continue;
      }

      const orderUuid = item.point.order.uuid;
      if (!orderUuid) continue;

      const isDedicated = SwapHelper.isDedicated(item);

      if (transferredOrdersUuid.has(orderUuid) && !passedSwapPoint) {
        return false;
      }

      if (isDedicated && activeLtlOrders.size > 0) return false;
      if (!isDedicated && activeFtlOrders.size > 0) return false;

      const category = item.point.point.category;
      const target = isDedicated ? activeFtlOrders : activeLtlOrders;

      if (category === OrderPointCategory.LOADING) {
        if (unloadedOrdersUuid.has(orderUuid)) return false;

        loadedOrdersUuid.add(orderUuid);
        target.set(orderUuid, (target.get(orderUuid) ?? 0) + 1);
      } else if (category === OrderPointCategory.UNLOADING) {
        if (!loadedOrdersUuid.has(orderUuid)) return false;

        const orderUnloadingPoints =
          orderPoints
            .get(orderUuid)
            ?.filter(
              ({ point }) => point?.category === OrderPointCategory.UNLOADING,
            ) ?? [];
        const isLast =
          item.point.point.uuid === orderUnloadingPoints.at(-1)?.point.uuid;
        if (isDedicated && orderUnloadingPoints.length > 1 && !isLast) {
          return true;
        }

        const current = target.get(orderUuid) ?? 0;
        if (current <= 1) {
          target.delete(orderUuid);
          loadedOrdersUuid.delete(orderUuid);
          unloadedOrdersUuid.add(orderUuid);
        } else {
          target.set(orderUuid, current - 1);
        }
      }

      if (activeFtlOrders.size > 1) return false;

      if (lastOrderPointIndex.get(orderUuid) === index) {
        activeFtlOrders.delete(orderUuid);
        activeLtlOrders.delete(orderUuid);
        loadedOrdersUuid.delete(orderUuid);
      }
    }

    return true;
  }

  public static canDropBeforeTarget(
    route: SwapRoutePoint[],
    dragPoint: SwapRoutePoint,
    targetPoint?: SwapRoutePoint,
  ): boolean {
    if (!targetPoint || targetPoint.isSwapPoint) return true;

    const targetOrderUuid = SwapHelper.getPointOrderUuid(targetPoint);
    if (!targetOrderUuid || !SwapHelper.isDedicated(targetPoint)) {
      return true;
    }

    const targetIndex = route.findIndex(
      (point) =>
        SwapHelper.getPointUuid(point) === SwapHelper.getPointUuid(targetPoint),
    );
    if (targetIndex <= 0) return true;

    const previousRoutePoint = route
      .slice(0, targetIndex)
      .reverse()
      .find((point) => !point.isSwapPoint);
    if (!previousRoutePoint) return true;

    const isInsideDedicatedBlock =
      SwapHelper.getPointOrderUuid(previousRoutePoint) === targetOrderUuid;
    if (!isInsideDedicatedBlock) return true;

    if (dragPoint.isSwapPoint) return true;

    return SwapHelper.getPointOrderUuid(dragPoint) === targetOrderUuid;
  }

  public static reorderRoutePoints(
    route: SwapRoutePoint[],
    dragPoint: SwapRoutePoint,
    targetPoint?: SwapRoutePoint,
  ) {
    const draggedPoints = SwapHelper.getDraggablePoints(dragPoint, route);
    const draggedPointsUuid = new Set(
      draggedPoints.map((point) => SwapHelper.getPointUuid(point)),
    );

    const remainingRoute = route.filter(
      (point) => !draggedPointsUuid.has(SwapHelper.getPointUuid(point)),
    );

    let insertIndex = remainingRoute.length;

    if (targetPoint) {
      const targetUuid = SwapHelper.getPointUuid(targetPoint);
      const index = remainingRoute.findIndex(
        (point) => SwapHelper.getPointUuid(point) === targetUuid,
      );
      if (index !== -1) insertIndex = index;
    }

    const updatedRoute = cloneDeep(remainingRoute);
    updatedRoute.splice(insertIndex, 0, ...draggedPoints);

    return updatedRoute;
  }

  public static restorePointsByInitialMap(
    currentRoute: SwapRoutePoint[],
    pointsToRestore: SwapRoutePoint[],
    initialRouteOrderMap: Map<string, number>,
  ): SwapRoutePoint[] {
    const restoredPoints = orderBy(
      pointsToRestore,
      (point) =>
        initialRouteOrderMap.get(SwapHelper.getPointUuid(point)) ?? Infinity,
      'asc',
    );

    const updatedRoute = cloneDeep(currentRoute);

    for (const restoredPoint of restoredPoints) {
      const initialIndex = initialRouteOrderMap.get(
        SwapHelper.getPointUuid(restoredPoint),
      );
      let insertIndex = updatedRoute.length;

      if (initialIndex != null) {
        const foundIndex = updatedRoute.findIndex((point) => {
          const currentInitialIndex = initialRouteOrderMap.get(
            SwapHelper.getPointUuid(point),
          );

          return currentInitialIndex != null
            ? currentInitialIndex > initialIndex
            : !point.isSwapPoint;
        });

        if (foundIndex !== -1) insertIndex = foundIndex;
      }

      updatedRoute.splice(insertIndex, 0, restoredPoint);
    }

    return updatedRoute;
  }

  public static getVehicleRoute(
    vehicle: TelematicsItem | null,
    isDouble: boolean,
    swapPointUuid: string,
  ) {
    if (!vehicle) return { route: [], initialRoute: [], formRoute: [] };

    const swapPoint: SwapRoutePoint = {
      point: {
        uuid: swapPointUuid,
        type: isDouble ? SwapPointType.DOUBLE : SwapPointType.SINGLE,
        category: TechnicalPointCategory.SWAP_POINT,
      },
      isSwapPoint: true,
    };

    const vehicleRoutePoints = chain(vehicle.routePoints)
      .filter(
        (point): point is TelematicsOrderRoutePoint =>
          !RouteHelper.isTechnicalPoint(point),
      )
      .filter((point) => !RouteHelper.isProposition(point))
      .filter(({ status }) => status !== TelematicsRoutePointStatus.CANCELED)
      .compact()
      .value();

    const uncompletedRoutePoints = vehicleRoutePoints.filter(
      ({ status }) => status !== TelematicsRoutePointStatus.COMPLETED,
    );
    const uncompletedPoints = uncompletedRoutePoints.map(
      (point): SwapRoutePoint => ({ point, isSwapPoint: false }),
    );

    const activeOrdersUuid = new Set(
      uncompletedRoutePoints.map(({ order }) => order.uuid),
    );

    const completedPoints = vehicleRoutePoints
      .filter(
        (point) =>
          point.status === TelematicsRoutePointStatus.COMPLETED &&
          point.order?.uuid &&
          activeOrdersUuid.has(point.order.uuid),
      )
      .map((point): SwapRoutePoint => ({ point, isSwapPoint: false }));

    const initialRoutePoints = [
      ...completedPoints,
      ...uncompletedPoints,
    ].filter((point) => !point.isSwapPoint);

    const initialRoute = initialRoutePoints.length
      ? initialRoutePoints.with(0, {
          ...initialRoutePoints[0],
          point: {
            ...initialRoutePoints[0].point,
            route: { ...initialRoutePoints[0].point.route, legs: [] },
          },
        })
      : [];

    const activePointIndex = uncompletedPoints.findLastIndex((point) =>
      SwapHelper.isActivePoint(point),
    );
    const route: SwapRoutePoint[] = [
      ...completedPoints,
      ...uncompletedPoints.slice(0, activePointIndex + 1),
      swapPoint,
      ...uncompletedPoints.slice(activePointIndex + 1),
    ];

    return {
      route,
      initialRoute,
      formRoute: SwapFormHelper.getFormRoute(route, vehicle),
    };
  }

  public static getVehicleInitialRouteOrderMap(
    vehicle: TelematicsItem | null,
    isDouble: boolean,
    swapPointUuid: string,
  ) {
    const { route: initialRoute } = SwapHelper.getVehicleRoute(
      vehicle,
      isDouble,
      swapPointUuid,
    );

    return new Map(
      initialRoute.map((point, index) => [
        SwapHelper.getPointUuid(point),
        index,
      ]),
    );
  }

  public static getVehicleInitialDriversMap(
    vehicle: Fleet | null,
  ): Map<string, number> {
    const drivers = chain([
      vehicle?.drivers?.primaryDriver,
      vehicle?.drivers?.secondaryDriver,
    ])
      .compact()
      .value();

    return new Map(drivers.map(({ uuid }, index) => [uuid, index]));
  }

  public static getActiveOrdersBeforeSwapPoint(
    route: SwapRoutePoint[],
  ): TelematicsRoutePointOrder[] {
    const swapIndex = SwapHelper.getSwapPointIndex(route);
    const beforeSwapPoints = route.slice(0, swapIndex);
    const afterSwapPointsUuid = SwapHelper.getOrdersUuid(
      route.slice(swapIndex + 1),
    );

    const activeOrdersMap = new Map<string, TelematicsRoutePointOrder>();

    for (const item of beforeSwapPoints) {
      if (item.isSwapPoint || !item.point.order) continue;
      const orderUuid = item.point.order.uuid;

      if (afterSwapPointsUuid.has(orderUuid)) {
        activeOrdersMap.set(orderUuid, item.point.order);
      }
    }

    return Array.from(activeOrdersMap.values());
  }

  public static getDraggablePoints(
    point: SwapRoutePoint,
    route: SwapRoutePoint[],
  ): SwapRoutePoint[] {
    if (!SwapHelper.isDedicated(point)) return [point];

    return route.filter(
      (routePoint) =>
        !routePoint.isSwapPoint &&
        SwapHelper.getPointOrderUuid(routePoint) ===
          SwapHelper.getPointOrderUuid(point),
    );
  }

  public static isMatchingReason(
    itemPoint: TelematicsOrderRoutePoint,
    targetPoint: TelematicsOrderRoutePoint,
    reason: SwapUnassignPointReason,
  ): boolean {
    return reason === SwapUnassignPointReason.POINT
      ? itemPoint.point.uuid === targetPoint.point.uuid
      : itemPoint.order?.uuid === targetPoint.order?.uuid;
  }

  public static getOrderLabel(
    item: TelematicsOrderRoutePoint | TelematicsRoutePointOrder | Order | null,
  ): { type: string; identifier: string } {
    if (!item) return { type: '', identifier: '' };

    const order = 'order' in item ? item.order : item;
    return {
      type: RouteHelper.isOrderDedicated(order) ? 'FTL' : 'LTL',
      identifier: order.identifier,
    };
  }

  public static hasCargoSpace(
    vehicle: Fleet | null,
    trailer: Fleet | null,
    trailerAction: SwapSetAction,
  ): boolean {
    if (!!trailer && trailerAction !== SwapSetAction.DROP_OFF_IN_POINT) {
      return true;
    }

    const category = vehicle?.generalInformation?.category;
    if (!category) return false;

    return (
      !!vehicle?.cargoSpace &&
      [FleetCategory.VAN, FleetCategory.STRAIGHT_TRUCK].includes(category)
    );
  }

  public static shouldResetAllVehicles(
    primarySet: SwapSet,
    secondarySet: SwapSet,
    isDouble: boolean,
  ): boolean {
    if (!isDouble) return false;

    const MULTI_SET_ACTIONS = [SwapSetAction.SWAP, SwapSetAction.TRANSFER];

    const checkSet = (setData: SwapSet): boolean =>
      Object.values(setData).some((data: SwapSetData<unknown>) => {
        if (isArray(data)) {
          return data.some((item) => MULTI_SET_ACTIONS.includes(item.action));
        }

        return MULTI_SET_ACTIONS.includes(data.action);
      });

    return checkSet(primarySet) || checkSet(secondarySet);
  }

  public static isFirstPointOfOrder(
    points: SwapRoutePoint[],
    item: SwapRoutePoint,
    index: number,
  ): boolean {
    if (item.isSwapPoint || !item.point.order?.isDedicated) return true;

    const previousPoint = points
      .slice(0, index)
      .reverse()
      .find((point) => !point.isSwapPoint);

    return (
      !previousPoint ||
      SwapHelper.getPointOrderUuid(previousPoint) !==
        SwapHelper.getPointOrderUuid(item)
    );
  }

  public static getTransferInsertIndex(route: SwapRoutePoint[]): number {
    const swapIndex = SwapHelper.getSwapPointIndex(route);

    const firstDedicatedPointBeforeSwap = route
      .slice(0, swapIndex)
      .reverse()
      .find((point) => SwapHelper.isDedicated(point));
    const anchorOrderUuid = firstDedicatedPointBeforeSwap
      ? SwapHelper.getPointOrderUuid(firstDedicatedPointBeforeSwap)
      : null;
    if (!anchorOrderUuid) return swapIndex + 1;

    let insertIndex = swapIndex + 1;

    while (
      insertIndex < route.length &&
      !route[insertIndex].isSwapPoint &&
      SwapHelper.isDedicated(route[insertIndex]) &&
      SwapHelper.getPointOrderUuid(route[insertIndex]) === anchorOrderUuid
    ) {
      ++insertIndex;
    }

    return insertIndex;
  }

  public static canSwapTrailers(
    sourceVehicle: TelematicsItem | null,
    sourceTrailer: Fleet | null,
    targetVehicle: TelematicsItem | null,
    targetTrailer: Fleet | null,
  ): boolean {
    if (!sourceVehicle || !targetVehicle) return false;
    if (!sourceTrailer || !targetTrailer) return false;

    return (
      this.isTrailerCompatible(sourceVehicle, targetTrailer) &&
      this.isTrailerCompatible(targetVehicle, sourceTrailer)
    );
  }

  public static isTrailerCompatible(
    vehicle: TelematicsItem | null,
    trailer: Fleet,
  ): boolean {
    if (!vehicle) return false;

    const { category } = vehicle.generalInformation;
    const trailerCategory = trailer.generalInformation.category;
    const { semiTrailerSize, trailerSize } = trailer.additionalParameters;

    if (category === FleetCategory.SEMI_TRUCK) {
      return (
        trailerCategory === FleetCategory.SEMI_TRAILER &&
        vehicle.parameters.isLowDeck ===
          (semiTrailerSize === SemiTrailerSize.MEGA)
      );
    }

    if (trailerCategory !== FleetCategory.TRAILER) return false;
    if (category === FleetCategory.VAN) return trailerSize === TrailerSize.VAN;
    if (category === FleetCategory.STRAIGHT_TRUCK) {
      return trailerSize === TrailerSize.STRAIGHT_TRUCK;
    }

    return false;
  }

  public static buildSwapPoint(
    uuid: string,
    address: OnyxCustomAddress,
    translateService: TranslateService,
  ): TelematicsRoutePoint {
    return {
      cycleIndex: -1,
      route: RouteHelper.createEmptyRoute(address.coordinates),
      order: null,
      point: null,
      status: TelematicsRoutePointStatus.ASSIGNED,
      deadlineTime: null,
      bufferTime: null,
      trailerUuid: null,
      primaryDriverDelegationUuid: null,
      secondaryDriverDelegationUuid: null,
      technicalPoint: {
        uuid,
        address,
        timeWindows: {
          type: OrderTimeWindowType.FIX,
          windows: [],
        },
        category: TechnicalPointCategory.SWAP_POINT,
        type: SwapPointType.SINGLE,
        name: translateService.instant(
          `${DictionaryCode.TECHNICAL_POINT_CATEGORY}.${TechnicalPointCategory.SWAP_POINT}`,
        ),
        status: OnyxMapRoutePointStatus.TO_DO,
        swapData: {
          identifier: '',
          primaryDriver: null,
          secondaryDriver: null,
          trailer: null,
          uuid: '',
        },
        estimations: null,
        timestamps: {
          arrivedAt: null,
          canceledAt: null,
          completedAt: null,
          startedAt: null,
        },
        route: RouteHelper.createEmptyRoute(address.coordinates),
      },
    };
  }

  public static mapToState(
    type: SwapVehicleType,
    data: Partial<SwapVehicleData>,
  ): Partial<SwapState> {
    if (type === SwapVehicleType.PRIMARY) {
      return {
        ...(data.vehicle && { vehicle: data.vehicle }),
        ...(data.route && { vehicleRoute: data.route }),
        ...(data.initialRoute && { vehicleInitialRoute: data.initialRoute }),
        ...(data.swapPointUuid && { vehicleSwapPointUuid: data.swapPointUuid }),
        ...(data.unassignedPoints && {
          vehicleUnassignedPoints: data.unassignedPoints,
        }),
        ...(data.trailerPoints && {
          vehicleSemiTrailerPoints: data.trailerPoints,
        }),
        ...(data.set && { vehicleSet: data.set }),
      };
    }

    return {
      ...(data.vehicle && { otherVehicle: data.vehicle }),
      ...(data.route && { otherVehicleRoute: data.route }),
      ...(data.initialRoute && { otherVehicleInitialRoute: data.initialRoute }),
      ...(data.swapPointUuid && {
        otherVehicleSwapPointUuid: data.swapPointUuid,
      }),
      ...(data.unassignedPoints && {
        otherVehicleUnassignedPoints: data.unassignedPoints,
      }),
      ...(data.trailerPoints && {
        otherVehicleSemiTrailerPoints: data.trailerPoints,
      }),
      ...(data.set && { otherVehicleSet: data.set }),
    };
  }

  public static getInitialState(): SwapState {
    return {
      showSwapPanel: false,

      isDouble: false,
      isRecorded: false,
      isFetching: false,

      filters: SwapHelper.getDefaultFilters(),
      address: null,
      fetchingItemsCount: 0,

      availableOrders: [],
      availableDrivers: [],
      availableTrailers: [],

      vehicle: null,
      vehicleRoute: [],
      vehicleInitialRoute: [],
      vehicleSwapPointUuid: uuidv7(),
      vehicleUnassignedPoints: [],
      vehicleSemiTrailerPoints: [],
      vehicleSet: SwapHelper.createSet(null, []),

      otherVehicle: null,
      otherVehicleRoute: [],
      otherVehicleInitialRoute: [],
      otherVehicleSwapPointUuid: uuidv7(),
      otherVehicleUnassignedPoints: [],
      otherVehicleSemiTrailerPoints: [],
      otherVehicleSet: SwapHelper.createSet(null, []),
    };
  }

  public static getDefaultFilters(): SwapFilters {
    return {
      isTrailerSwap: false,
      isDriversSwap: false,
      checkDailyDriving: false,
      remainingDailyDriving: Duration.fromObject({ hours: 4 }).as('seconds'),
      checkRouteRange: false,
      routeRange: 50 * 10 ** 3,
    };
  }
}
